<template>
  <div class="filterGroup" :class="{ 'filterGroup--running': running, 'filterGroup--dashed': dashed }">
    <!-- Shimmer bar for running state -->
    <div v-if="running" class="filterGroupShimmer" />

    <div v-if="label" class="filterGroupLabelRow">
      <span class="filterGroupLabel">{{ label }}</span>
      <slot name="corner-badge" />
    </div>

    <div v-if="running" class="filterGroupRunningBadge">
      <span>Running</span>
    </div>

    <div class="filterGroupBody">
      <slot />
    </div>

    <!-- Footer with last run + controls — omitted entirely when unused,
         e.g. a single source/prospect node owns its own card-level
         footer instead of a shared group footer. -->
    <div v-if="hasFooter" class="filterGroupFooter">
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSlots } from 'vue'

defineProps<{
  running?: boolean
  label?: string
  /** Empty-group styling in edit mode: dashed border, no solid fill. */
  dashed?: boolean
}>()

const slots = useSlots()
const hasFooter = Boolean(slots.footer)
</script>

<style scoped>
.filterGroup {
  position: relative;
  width: fit-content;
  min-width: 100%;
  border: 2px solid var(--color-border-strong);
  border-radius: var(--radius-xl, 16px);
  background: var(--color-bg-app);
  overflow: visible;
  --filter-group-label-height: calc(var(--space-4) + var(--fs-sm) * var(--lh-tight));
}

.filterGroup--running {
  border-color: var(--color-accent);
}

.filterGroup--dashed {
  border-style: dashed;
  border-width: 3px;
  border-color: var(--color-border-strong);
}

.filterGroupShimmer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: var(--radius-xl, 16px) var(--radius-xl, 16px) 0 0;
  overflow: hidden;
  background: var(--color-accent-soft, rgba(5, 150, 105, 0.15));
}
.filterGroupShimmer::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, var(--color-accent), transparent);
  animation: filterShimmer 1.5s ease-in-out infinite;
}
@keyframes filterShimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}

.filterGroupRunningBadge {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-accent);
  padding: 2px var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-accent-soft, rgba(5, 150, 105, 0.1));
}

.filterGroupLabelRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-4) 0;
}

.filterGroupLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-tight);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-text-primary);
}

.filterGroupBody {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: var(--space-4) var(--space-4) var(--space-5);
}

/* Connectors and the trailing add button should center, same axis. */
.filterGroupBody :deep(.connector),
.filterGroupBody :deep(.addNodeWrap) {
  align-self: center;
}

.filterGroupFooter {
  border-top: 2px solid var(--color-border-strong);
}
</style>
